import { map } from "lodash";
import {
  ref,
  child,
  get,
  onValue,
  query,
  orderByChild,
  equalTo,
  push,
  set,
  remove,
  runTransaction,
  update,
} from "firebase/database";
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { database, storage, auth } from "firebase_config";
import { planInventoryOps } from "inventoryPlan";

export const AUTH_USER = "AUTH_USER";
export const UNAUTH_USER = "UNAUTH_USER";

export const FETCH_JOBS = "FETCH_JOBS";
export const REMOVE_JOB = "REMOVE_JOB";
export const FETCH_JOB_ITEMS = "FETCH_JOB_ITEMS";
export const FETCH_ITEM_TYPES = "FETCH_ITEM_TYPES";
export const FETCH_SETTINGS = "FETCH_SETTINGS";
export const FETCH_MATERIALS = "FETCH_MATERIALS";
export const FETCH_JOB_REPORT = "FETCH_JOB_REPORT";
export const FETCH_JOB_REPORTS = "FETCH_JOB_REPORTS";
export const SET_OPEN_JOB = "SET_OPEN_JOB";

const dbRef = ref(database);
const assetConstruction = child(dbRef, "assetConstruction");
const users = child(dbRef, "users/users");
const buildJobs = child(assetConstruction, "jobs");
const jobItems = child(assetConstruction, "jobItems");
const jobReports = child(assetConstruction, "jobReports");
const jobItemTypes = child(assetConstruction, "jobItemTypes");
const settings = child(assetConstruction, "settings");
const materials = child(assetConstruction, "materials");

// =================================
// ==========Job Actions===========
// =================================

// Subscription actions (fetch*) attach a realtime listener and RETURN the
// unsubscribe function, so components can detach it in a useEffect cleanup.
// Call them as: useEffect(() => dispatch(fetchX(...)), [dispatch, ...]).
// Previously every call attached a listener that was never removed, so
// listeners (and duplicate dispatches) accumulated for the whole session.
export function fetchJobs(closed) {
  return (dispatch) => {
    const includeClosed = closed
      ? buildJobs
      : query(query(buildJobs, orderByChild("status")), equalTo("open"));
    return onValue(includeClosed, (snapshot) => {
      dispatch({
        type: FETCH_JOBS,
        payload: snapshot.val(),
      });
    });
  };
}

export function createJob(values, cb) {
  return () => {
    const jobId = push(buildJobs).key;
    set(child(buildJobs, jobId), {
      ...values,
      jobId,
    }).then(cb);
  };
}

export function updateJob(values, cb) {
  return () => {
    const { jobId } = values;
    // The live fetchJobs listener picks up the change; re-dispatching
    // fetchJobs here just leaked an extra listener pinned to open-only.
    return set(child(buildJobs, jobId), {
      ...values,
      jobId,
    }).then(() => cb());
  };
}
export function deleteJob(jobId, cb) {
  return (dispatch) => {
    remove(child(buildJobs, jobId)).then(() => {
      dispatch({
        type: REMOVE_JOB,
        payload: jobId,
      });
      return cb();
    });
  };
}

export function setOpenJobId(id) {
  return dispatch => {
    dispatch({
      type: SET_OPEN_JOB,
      payload: id
    })
  }
}

// =================================
// ==========Item Actions===========
// =================================

export function fetchJobItems(jobId) {
  return (dispatch) => {
    if (!jobId) return () => {};
    return onValue(
      query(query(jobItems, orderByChild("jobId")), equalTo(jobId)),
      (snapshot) => {
        dispatch({
          type: FETCH_JOB_ITEMS,
          payload: snapshot.val(),
        });
      }
    );
  };
}

export function createJobItem(values, cb) {
  return () => push(jobItems, values).then(() => cb && cb());
}

export function updateJobItem(values, itemId, cb) {
  return () => update(child(jobItems, itemId), values).then(cb);
}

export function batchUpdateJobItems(values, cb) {
  return () => update(jobItems, values).then(cb);
}
export function deleteJobItem(itemId, cb) {
  return () => remove(child(jobItems, itemId));
}

export function fetchItemTypes() {
  return (dispatch) => {
    return onValue(jobItemTypes, (snapshot) => {
      dispatch({
        type: FETCH_ITEM_TYPES,
        payload: snapshot.val(),
      });
    });
  };
}

// =================================
// ==========Report Actions===========
// =================================

export function fetchJobReports(jobId) {
  return (dispatch) => {
    if (!jobId) return () => {};
    return onValue(
      query(query(jobReports, orderByChild("jobId")), equalTo(jobId)),
      (snapshot) => {
        dispatch({
          type: FETCH_JOB_REPORTS,
          payload: snapshot.val(),
        });
      }
    );
  };
}

export function fetchJobReport(reportId) {
  return (dispatch) => {
    return onValue(child(jobReports, reportId), (snapshot) => {
      dispatch({
        type: FETCH_JOB_REPORT,
        payload: { [reportId]: snapshot.val() },
      });
    });
  };
}

export function updateJobReport(values, reportId, cb) {
  return () => set(child(jobReports, reportId), values).then(cb);
}

export function createJobReport(values, cb) {
  return () => push(jobReports, values).then(() => cb && cb());
}
// =================================
// ========Materials Actions========
// =================================
export function fetchMaterials(all) {
  return (dispatch) =>
    onValue(materials, (snapshot) => {
      dispatch({
        type: FETCH_MATERIALS,
        payload: snapshot.val(),
      });
    });
}

export function deleteMaterial(id, cb) {
  return () => remove(child(materials, id)).then(cb);
}

export function removeMaterialInventory(localItem, currentItem, cb) {
  // Plan-then-commit. All qty/unit validation happens in planInventoryOps
  // BEFORE any stock is touched, so a bad unit or qty aborts the whole update
  // with nothing deducted. (Previously parts were adjusted concurrently: a
  // failure partway through left earlier parts deducted with no rollback, and
  // because the item save was blocked, every retry deducted them again.)
  return async () => {
    // Gather every material referenced by a changed link.
    const ids = new Set();
    ["connections", "bodies"].forEach((type) =>
      map(localItem[type] || [], (part, partIndex) => {
        const prevPart = (currentItem[type] || [])[partIndex];
        const prevId = (prevPart && prevPart.materialId) || "";
        const nextId = part.materialId || "";
        if (nextId === prevId) return;
        if (nextId) ids.add(nextId);
        if (prevId) ids.add(prevId);
      })
    );

    const materialsById = {};
    await Promise.all(
      [...ids].map(async (id) => {
        const snap = await get(child(materials, id));
        materialsById[id] = snap.exists()
          ? { exists: true, um: snap.val().um || "" }
          : { exists: false };
      })
    );

    const ops = planInventoryOps(localItem, currentItem, materialsById);

    // Number(count): legacy qty values were saved as strings, and
    // "4" + (-1) would concatenate instead of subtract.
    await Promise.all(
      ops.map(({ materialId, delta }) =>
        runTransaction(
          child(materials, `${materialId}/qty`),
          (count) => (Number(count) || 0) + delta
        )
      )
    );
    return cb();
  };
}

export function setMaterial(values, cb) {
  const id = values.id || push(materials).key;
  return () => {
    set(child(materials, id), { ...values, id }).then(cb);
  };
}

// =================================
// ========Settings Actions=========
// =================================

export function setSettingValue(type, values, cb) {
  const id = values.id || push(child(settings, type)).key;
  const setting = child(child(settings, type), id);
  return () => set(setting, { ...values, id }).then(cb);
}

export function fetchSettings() {
  return (dispatch) =>
    onValue(settings, (snapshot) => {
      dispatch({
        type: FETCH_SETTINGS,
        payload: snapshot.val(),
      });
    });
}
export function setItemType(values, cb) {
  const hasId = Boolean(values.itemTypeId);
  const key = hasId ? values.itemTypeId : push(jobItemTypes).key;
  return () => set(child(jobItemTypes, key), values).then(() => cb(key));
}
// =================================
// ========Storage Actions==========
// =================================

export function uploadCert(itemId, asset, file, attachmentLocations, cb) {
  const fullPath = `assetConstruction/cocs/${itemId}/COC_${asset}.pdf`;
  return (dispatch) => {
    const fileRef = storageRef(storage, fullPath);

    const uploadTask = uploadBytesResumable(fileRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Observe state change events such as progress, pause, and resume
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Upload is " + progress + "% done");
        switch (snapshot.state) {
          case "paused":
            console.log("Upload is paused");
            break;
          case "running":
            console.log("Upload is running");
            break;
          default:
            break;
        }
      },
      (error) => {
        // Handle unsuccessful uploads
      },
      () => {
        // Handle successful uploads on complete
        // For instance, get the download URL: https://firebasestorage.googleapis.com/...
        getDownloadURL(uploadTask.snapshot.ref).then((downloadUrl) => {
          const coc = {
            fileLocation: fullPath,
            downloadUrl,
            fileName: `COC_${asset}.pdf`,
          };
          dispatch(
            updateJobItem({ coc, attachmentLocations }, itemId, () =>
              console.log("item updated")
            )
          );
        });
      }
    );
  };
}

export function uploadFile(uploadLocation, file, cb) {
  const uniqKey = push(jobItems).key;
  const fullPath = `${uploadLocation}/${uniqKey}/${file.name}`;
  return () => {
    const fileRef = storageRef(storage, fullPath);
    const uploadTask = uploadBytesResumable(fileRef, file);

    // Register three observers:
    // 1. 'state_changed' observer, called any time the state changes
    // 2. Error observer, called on failure
    // 3. Completion observer, called on successful completion
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        // Observe state change events such as progress, pause, and resume
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log("Upload is " + progress + "% done");
        switch (snapshot.state) {
          case "paused":
            console.log("Upload is paused");
            break;
          case "running":
            console.log("Upload is running");
            break;
          default:
            break;
        }
      },
      (error) => {
        // Handle unsuccessful uploads
      },
      () => {
        // Handle successful uploads on complete
        // For instance, get the download URL: https://firebasestorage.googleapis.com/...
        getDownloadURL(uploadTask.snapshot.ref).then((downloadUrl) => {
          cb({
            fileLocation: fullPath,
            downloadUrl,
            fileName: file.name,
          });
        });
      }
    );
  };
}

export function deleteFile(fullFilePath, cb) {
  return () => {
    const fileRef = storageRef(storage, fullFilePath);
    deleteObject(fileRef)
      .then(cb)
      .catch((e) => console.log(e));
  };
}
// =================================
// ==========Auth Actions===========
// =================================

export function signIn(values, cb) {
  const { email, password } = values;
  return () =>
    signInWithEmailAndPassword(auth, email, password)
      .then((res) => {
        cb({ type: "User", res });
      })
      .catch((res) => cb({ type: "Error", res }));
}

export function logoutUser(cb) {
  // onAuthStateChanged dispatches UNAUTH_USER, which re-locks the routes.
  return () =>
    signOut(auth)
      .then(() => cb && cb())
      .catch((err) => cb && cb(err));
}

// Subscribes to Firebase auth state for the life of the app and keeps the
// authUser slice in sync; returns onAuthStateChanged's unsubscribe function.
// The SDK owns session persistence — this no longer hand-writes the SDK's own
// localStorage key, which could drift from what the SDK expected.
export function verifyAuth() {
  return (dispatch) => {
    return onAuthStateChanged(auth, (user) => {
      if (user && user.uid) {
        const uid = user.uid;
        const uChild = child(users, uid);
        get(uChild)
          .then((snapshot) => {
            const dbUser = snapshot.val();
            dispatch({
              type: AUTH_USER,
              payload: { ...user, ...dbUser },
            });
          })
          .catch((err) => {
            // Profile lookup can fail transiently (e.g. RTDB briefly offline at
            // sign-in). Authenticate with the token identity so the app still
            // loads instead of throwing an unhandled rejection.
            console.warn("User profile lookup failed; using auth identity", err);
            dispatch({
              type: AUTH_USER,
              payload: { ...user },
            });
          });
      } else {
        dispatch({
          type: UNAUTH_USER,
        });
      }
    });
  };
}
